'use strict';

import Evented from '../ui/modules/evented';
import MapUIContainer from './modules/map-ui-container';

export default class MapUI extends Evented {

  constructor() {

    super();

    this._containers = [];

    this._containers_to_load = [];
    this._files_loaded = [];

    this._initializing = [];

    this.color = {

      orange: '#f39c12',
      green: '#149a80',
      blue: '#4ea0da',
      purple: '#8584c7',
      red: '#e74c3c',

    };

    this._engines = {

      mapbox_js: '//api.tiles.mapbox.com/mapbox-gl-js/v1.11.1/mapbox-gl.js',
      mapbox_css: '//api.tiles.mapbox.com/mapbox-gl-js/v1.11.1/mapbox-gl.css',

      d3_js: `https://d3js.org/d3.v5.min.js`,

    };

    this._styles = {

      street: 'mapbox://styles/iambz/ck6liwcbb09fm1ip2qs8rmedc?fresh=true',
      terrain: 'mapbox://styles/iambz/ck69y8it22otr1iqh8sdni5ri?fresh=true',
      hybrid: 'mapbox://styles/iambz/ck7j863ap20uz1isxoxmgra73?fresh=true',

    };
    this._styles.default = this._styles.terrain;

  }

  //
  // Public
  //

  load( map_init ) {
    this._containers_to_load = map_init;
    this._prepare_load();
  };

  instance( options ) {
    return new MapUIContainer( options );
  };

  //
  // Private
  //

  async _load_file_if( src ) {
    return new Promise( async ( resolve, reject ) => {

      if ( -1 === this._files_loaded.indexOf( src ) ) {
        await this._load_file( src );
      }
      resolve();

    } );
  };
  async _load_data( data ) {

    const headers = {};
    const extension = data.split( '.' ).pop();

    switch ( extension ) {
      case 'kml':
        headers.Accept = 'application/vnd.google-earth.kml+xml';
        break;
      case 'gpx':
        headers.Accept = 'application/gpx+xml';
        break;
    }

    return await _f.load( data, 'get', null, headers );

  };
  async _load_file( src ) {
    return new Promise( ( resolve, reject ) => {

      let script;
      const re = /(?:\.([^.?]+))?(\?.*)?$/;
      const ext = re.exec( src )[ 1 ];

      switch ( ext ) {

        case 'js':
          script = document.createElement( 'script' );
          script.src = src;
          script.onload = resolve;
          this._files_loaded.push( src );
          document.getElementsByTagName( 'head' )[ 0 ].appendChild( script );
          break;

        // http://stackoverflow.com/questions/5537622/dynamically-loading-css-file-using-javascript-with-callback-without-jquery
        case 'css':
          const head = document.getElementsByTagName( 'head' )[ 0 ];
          const link = document.createElement( 'link' );
          link.setAttribute( 'href', src );
          link.setAttribute( 'rel', 'stylesheet' );
          link.setAttribute( 'type', 'text/css' );
          this._files_loaded.push( src );
          head.appendChild( link );
          resolve();
          break;

        case 'jsonp':
          throw new Error( `JSONP load not implemented (${ext}, ${src})` );
          break;

        default:
          throw new Error( `Cannot load file (${ext}, ${src})` );

      }

    } );
  };

  async _wait_for_container_in_view( id ) {
    return new Promise( ( resolve, reject ) => {

      const observer = new IntersectionObserver( ( entries, observer ) => {
        if ( entries[ 0 ].isIntersecting ) {
          observer.disconnect();
          return resolve();
        }
      }, {
        rootMargin: '200px 0px 200px 0px',
      } );
      observer.observe( document.querySelector( `#${id}` ) );
      _wp.c.d && console.debug( `-- waiting for container #${id} to be in view` );

    } );
  }

  async _sleep( ms ) {
    return new Promise( resolve => setTimeout( resolve, ms ) );
  }

  async _wait_for_clear_queue() {
    return new Promise( async ( resolve, reject ) => {

      let max_wait = 10;

      do {
        if ( 0 === this._initializing.length ) {
          return resolve();
        }
        await this._sleep( 1000 );
      }
      while ( --max_wait > 0 );

      throw new Error( `Done waiting for map loading queue to be clear, and it did not happen in time. :(` );

    } );
  }

  async _prepare_load() {

    this.on( 'map.pre_init', ( e ) => {
      e.container.options.id = this._initializing[ 0 ].options.id;
      e.container.options.dom_id = `map-${this._initializing[ 0 ].options.id}`;
      this._containers.push( e.container );
      this._initializing.pop();
    } );

    if ( 'object' === typeof this._containers_to_load && Array.isArray( this._containers_to_load ) && this._containers_to_load.length > 0 ) {

      this._containers_to_load.map( data => {
        return async function() {

          if ( data && undefined !== data.options && undefined !== data.options.id ) {

            try {
              // This should be a limiter too, we cannot go forward if a map is already in the load (this._initializing > 0)
              await this._wait_for_container_in_view( `map-${data.options.id}` );
              _wp.c.d && console.debug( `-- container #map-${data.options.id} is in view!` );
              await this._wait_for_clear_queue();
            }
            catch ( e ) {
              console.error( e );
              return;
            }

            this._initializing.push( data );

            // Load data file
            if ( undefined !== data.options.src && undefined !== data.options.src ) {
              // For the new good guys, nothing to do. The new maps should
              // self-spawn, calling the constructor on their own
              await this._load_file( data.options.src );
            }
            // No data file
            // Self contained options for map
            else {
              new map_ui.instance( data.options );
            }

          }

        }.bind( this )();

      } );

    }

  };

}

window.addEventListener( 'late-init-done', () => {

  window.map_ui = window.map_ui || new MapUI();

  if ( 'undefined' !== typeof map_init ) {
    window.map_ui.load( map_init );
  }

} );
